// Insights into top-funnel SEO landing pages (public, indexable).
// All META preset for useDocumentMeta hook.
import { useEffect } from "react";

const META_TAGS = [
  ["name", "description"],
  ["name", "keywords"],
  ["property", "og:title"],
  ["property", "og:description"],
  ["property", "og:url"],
  ["name", "twitter:title"],
  ["name", "twitter:description"],
];

function setOrCreateMeta(attr, key, value) {
  if (value == null) return null;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  const created = !el;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  const prev = el.getAttribute("content");
  el.setAttribute("content", value);
  return { el, prev, created };
}

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  const created = !el;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  const prev = el.getAttribute("href");
  el.setAttribute("href", href);
  return { el, prev, created };
}

export default function useDocumentMeta({
  title, description, keywords, canonical,
  ogTitle, ogDescription, twitterTitle, twitterDescription,
}) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const restores = [];
    const apply = (attr, key, value) => {
      const r = setOrCreateMeta(attr, key, value);
      if (r) restores.push({ attr, key, ...r });
    };

    apply("name", "description", description);
    apply("name", "keywords", keywords);
    apply("property", "og:title", ogTitle || title);
    apply("property", "og:description", ogDescription || description);
    if (canonical) apply("property", "og:url", canonical);
    apply("name", "twitter:title", twitterTitle || title);
    apply("name", "twitter:description", twitterDescription || description);

    let canonRestore = null;
    if (canonical) canonRestore = setCanonical(canonical);

    return () => {
      document.title = prevTitle;
      restores.forEach((r) => {
        if (r.created) r.el.remove();
        else if (r.prev != null) r.el.setAttribute("content", r.prev);
      });
      if (canonRestore) {
        if (canonRestore.created) canonRestore.el.remove();
        else if (canonRestore.prev != null) canonRestore.el.setAttribute("href", canonRestore.prev);
      }
    };
  }, [title, description, keywords, canonical, ogTitle, ogDescription, twitterTitle, twitterDescription]);
}

// 2026 META presets — refreshed with current Indian property/loan SEO trends.
//
// Strategy:
//  • 8-12 high-intent keywords per route (avoid stuffing → Google quality penalty).
//  • Mix of head terms (EMI calculator) + long-tail conversational queries
//    ("should I rent or buy a flat in Bangalore 2026") for LLM AI Overview.
//  • Tie to 2026-specific anchors: Section 24B, RBI repo rate, CIBIL 725,
//    ₹50L home loan EMI, Rental Snowball, CLSS 2026 revival.
//  • Each title leads with the value prop, not the brand — better CTR.
export const META = {
  landing: {
    title: "Estima — Rent vs Buy, EMI Calculator, Rental Snowball & Property Portfolio Tracker (India 2026)",
    description: "India's AI-powered property decision engine. Calculate home loan EMI, decide rent vs buy, run a rental snowball plan to build a multi-property portfolio, compare flats side-by-side, and draft a lawyer-verified Will. Section 24B-aware, INR-first, 6-month free trial. Updated for Budget 2026.",
    keywords: "rent vs buy india 2026, home loan emi calculator india, rental snowball planner, property comparison india, section 24b deduction 2026, home loan interest rate india 2026, repo rate 2026, cibil 725 home loan, should i rent or buy india 2026, ₹50 lakh home loan emi, property roi calculator india, real estate investment india, ai property advisor india, mumbai bangalore delhi pune hyderabad property",
    canonical: "https://estima.co.in/",
  },
  pricing: {
    title: "Estima Pricing — Basic ₹499/mo or Pro ₹999/mo · EMI, Rent vs Buy, AI Advisor, Snowball Planner",
    description: "Estima plans for 2026: Basic at ₹499/month covers portfolio tracking + EMI; Pro at ₹999/month adds Rent vs Buy, AI Advisor, Loan Optimizer, Rental Snowball Planner, Rent Comps, and AI-drafted Will. Yearly options save 17%. Get a 6-month free trial when you sign up.",
    keywords: "estima pricing 2026, property tracker subscription india, home loan calculator app india, rent vs buy tool india, rental snowball app india, ai property advisor subscription, real estate saas india pricing",
    canonical: "https://estima.co.in/pricing",
  },
  lawyerRegister: {
    title: "Estima for Lawyers & Notaries — Will Drafting, Succession Planning, e-Sign Verification (India)",
    description: "Join Estima as a registered Indian lawyer or notary to review AI-drafted Wills, verify witnesses online, e-sign documents, and earn from succession-planning work. Built for the Indian Succession Act and Hindu Succession Act 1956. Manage your case-load from a clean dashboard. Razorpay payouts.",
    keywords: "lawyer registration india 2026, online will drafting india, notary will verification, succession planning lawyer india, hindu succession act 1956, indian succession act 1925, will registration online india, lawyer marketplace india, legal tech india, estate planning lawyer india, online legal services india",
    canonical: "https://estima.co.in/lawyer/register",
    ogTitle: "Lawyers & Notaries — Join Estima to draft and verify Wills online",
    ogDescription: "Estima connects registered Indian lawyers with clients drafting Wills. Review AI-prepared drafts, verify witnesses, e-sign, get paid via Razorpay.",
  },
  login: {
    title: "Sign in to Estima — Your Property Decision Dashboard (EMI, Rent vs Buy, Portfolio)",
    description: "Sign in to Estima to access your property portfolio, home loan EMI calculator, Rent vs Buy analysis, Rental Snowball Planner, AI investment advisor, Will drafts and more — India's INR-first real-estate decision engine.",
    keywords: "estima login, property dashboard india, real estate tracker login, home loan calculator login india",
    canonical: "https://estima.co.in/login",
  },
  register: {
    title: "Sign up free for Estima — 6-month trial · EMI, Rent vs Buy, Rental Snowball, AI Advisor",
    description: "Create a free Estima account and get a 6-month free trial. Calculate home loan EMI, decide rent vs buy, build a Rental Snowball portfolio, track your real estate, get AI investment advice. Updated for Budget 2026 — Section 24B, CIBIL 725, RBI repo rate. INR-first, India only.",
    keywords: "real estate signup india, property tracker signup, free emi calculator india, rent vs buy free trial india, rental snowball signup, ai property advisor signup, real estate app sign up 2026",
    canonical: "https://estima.co.in/register",
  },
  disclaimer: {
    title: "Estima Disclaimer — Estimates only, not financial advice",
    description: "Estima calculator outputs (EMI, Rent vs Buy, Rental Snowball, Rental Yield, Capital Gains) are indicative estimates only. Please consult a SEBI-registered investment advisor or qualified Chartered Accountant before any investment decision.",
    keywords: "estima disclaimer, real estate calculator disclaimer india, financial advice disclaimer, property estimate disclaimer",
    canonical: "https://estima.co.in/disclaimer",
  },
};
